"use client";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useStoreBuilderContext } from "@/ui/admin/store-builder/store-builder-context";
import { StoreBuilderUpload } from "@/ui/admin/store-builder/store-builder-upload";
import { debounce } from "@/ui/hooks/use-debounced-value";
import { StatefulInput } from "@/ui/stateful-input";
import { PlusCircle, Trash2Icon } from "lucide-react";
import { useState } from "react";
import type { YnsComponentForm } from "../types";
import type { InfoGrid } from "./info-grid";

export const InfoGridForm: YnsComponentForm<typeof InfoGrid> = ({ data }) => {
	const context = useStoreBuilderContext<typeof InfoGrid>();
	const initialItems = data.items;
	const [items, setItems] = useState(initialItems);

	const addItem = () => {
		const newItems = [
			...items,
			{
				title: "",
				description: "",
				image: { src: "" },
			},
		];
		setItems(newItems);
		context.saveSelectedPageSectionField({
			fieldPath: "items",
			value: newItems,
		});
	};

	const removeItem = (index: number) => {
		if (items.length <= 1) return; // Keep at least one item

		const newItems = items.filter((_, i) => i !== index);
		setItems(newItems);
		context.saveSelectedPageSectionField({
			fieldPath: "items",
			value: newItems,
		});
	};

	const updateItemField = (index: number, fieldPath: string, value: string, save = true) => {
		const newItems = [...items];

		// Handle nested paths like "button.label"
		const pathParts = fieldPath.split(".");
		let current = newItems[index];

		for (let i = 0; i < pathParts.length - 1; i++) {
			if (!current) return;
			const cursor = pathParts[i];
			if (!cursor) return;

			// @ts-ignore
			current = current[cursor];
		}

		// @ts-ignore
		current[pathParts[pathParts.length - 1]] = value;

		setItems(newItems);

		if (save) {
			context.saveSelectedPageSectionField({
				fieldPath: "items",
				value: newItems,
			});
		}
	};

	return (
		<div className="grid gap-4 relative pb-20">
			<Accordion type="multiple" className="w-full">
				{items.map((item, index) => (
					<AccordionItem key={index} value={`item-${index}`}>
						<div className="flex items-center justify-between">
							<AccordionTrigger className="flex-1">{item.title || "Untitled Item"}</AccordionTrigger>
							<Button
								variant="ghost"
								size="sm"
								onClick={(e) => {
									e.stopPropagation();
									removeItem(index);
								}}
								className={cn("mr-2", items.length <= 1 && "invisible")}
								disabled={items.length <= 1}
							>
								<Trash2Icon className="h-4 w-4" />
							</Button>
						</div>
						<AccordionContent>
							<div className="grid gap-4 pl-2 pt-4">
								<Label className="grid gap-2 mt-2">
									<span>Title</span>
									<StatefulInput
										required
										defaultValue={item.title}
										onValueChange={debounce(
											(value) => {
												updateItemField(index, "title", value);
											},
											300,
											{ trailing: true },
										)}
									/>
								</Label>
								<Label className="grid gap-2 mt-2">
									<span>Description</span>
									<StatefulInput
										required
										defaultValue={item.description}
										onValueChange={debounce(
											(value) => {
												updateItemField(index, "description", value);
											},
											300,
											{ trailing: true },
										)}
									/>
								</Label>
								<Label className="grid gap-2 mt-2">
									<span>Link</span>
									<StatefulInput
										defaultValue={item.url}
										onValueChange={debounce(
											(value) => {
												updateItemField(index, "url", value);
											},
											300,
											{ trailing: true },
										)}
									/>
								</Label>
								<div className="grid gap-2 mt-2">
									<div>
										<div>Image</div>
										<div className="text-xs text-neutral-600">
											Formats: PNG, JPG, WebP, AVIF ⋅ Dimensions: 1920x1080 (16:9) ⋅ Max size: 4.5 MB{" "}
										</div>
									</div>
									<StoreBuilderUpload
										width={1920}
										height={1080}
										className=""
										fieldPathPrefix={`items.${index}.image.`}
										images={[item.image?.src].filter(Boolean)}
										onUpload={(src) => {
											updateItemField(index, "image.src", src, false);
										}}
										onDelete={() => {
											updateItemField(index, "image.src", "", false);
										}}
									/>
								</div>
							</div>
						</AccordionContent>
					</AccordionItem>
				))}
			</Accordion>

			<Button
				variant="outline"
				className="flex items-center gap-2 w-full mt-4"
				onClick={addItem}
				disabled={items.length >= 4}
			>
				<PlusCircle className="h-4 w-4" />
				Add New Item
			</Button>
		</div>
	);
};
